import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { ScoreCard, ClubCard, CtaCard } from '../../cards';
import AdWrapper from '../../Ads/AdWrapper';
import Events from '../EventsTabContent/Events';
import LoadingText from '../../text/LoadingText';

import useIsMobile from '../../hooks/useIsMobile';
import { AdTypes } from '../../../utils/types';
import { EventScheduleTypes } from '../../../utils/eventConstants';
import { EventTypes } from '../../../utils/clubs';
import { getFindEventsSearchUrl } from '../../../utils/events';
import { getOverviewData, getClubsData } from '../../../actions/profile-tab-actions';
import { screens } from '../../PopUps/LoginPopUpWrapper/LoginPopUp/utils';
import { useAdDynamicConfig } from '../../hooks/useAdDynamicConfig';

import styles from '../../../pages/ProfilePage/profilePage.scss';

function OverviewTabContent({
    profile,
    auth,
    isProfileIdUpdated,
    isCurrentUserProfile,
    isProProfile,
    isUserLoggedIn,
    ctaData,
    search,
    onSeeEventsClick,
    onShowLoginPopUp,
}) {
    const history = useHistory();
    const dispatch = useDispatch();
    const [hasMountedWithoutUser, setHasMountedWithoutUser] = useState(false);
    const { data, isLoading } = useSelector(({ profileTabData }) => profileTabData?.overview);
    const { data: clubData } = useSelector(({ profileTabData }) => profileTabData?.clubs);
    const { RECENT, UPCOMING } = EventScheduleTypes;
    const { results, recentEvents, upcomingEvents } = data || {};
    const { recentClubs } = clubData || {};

    const { isMobileWidth } = useIsMobile();
    const { showAds, playerProfileConfig } = useAdDynamicConfig();
    const showRectangleAds = showAds && isMobileWidth && playerProfileConfig?.showRectangle;

    useEffect(() => {
        if (!isUserLoggedIn) {
            setHasMountedWithoutUser(true);
        }
    }, []);

    useEffect(() => {
        if (isUserLoggedIn && !data && isProfileIdUpdated && !hasMountedWithoutUser) {
            dispatch(getOverviewData());
        }
        if (isUserLoggedIn && !recentClubs && isProfileIdUpdated && !hasMountedWithoutUser) {
            dispatch(getClubsData());
        }
    }, [isProfileIdUpdated, isUserLoggedIn, hasMountedWithoutUser]);

    let eligibleResults;

    if (profile?.data?.extendedRatingProfile?.eligibleResults) {
        eligibleResults = [...profile.data.extendedRatingProfile.eligibleResults.singles, ...profile.data.extendedRatingProfile.eligibleResults.doubles];
    }

    const handleOnFindEventsClick = () => {
        const url = getFindEventsSearchUrl(search);
        history.push(url);
    };
    const firstName = profile?.data?.firstName;
    const lastCharacter = firstName.charAt(firstName.length - 1);
    const isNameEndingInS = lastCharacter.toLowerCase() === 's';
    const name = isNameEndingInS ? `${firstName}'` : `${firstName}'s`;

    if (!isUserLoggedIn) {
        return (
            <div className={`${styles.overviewSignInContainer}`} data-testid="profile.overview.sign-in-container">
                <p className={`${styles.overviewSignInTitle}`} data-testid="profile.overview.sign-in-title">{`Sign in or sign up to view ${name} profile`}</p>
                <p className={`${styles.overviewSignInSubtitle}`} data-testid="profile.overview.sign-in-subtitle">{`View ${name} match results, upcoming and previous events, player stats, and other information.`}</p>
                <button
                    className={`btn btn-primary btn-block btn-xl text-uppercase mt16`}
                    onClick={() => onShowLoginPopUp('signin', screens.LOGIN)}
                    type="button"
                    data-testid="profile.overview.sign-in-btn"
                >
                    Sign In
                </button>
                <button
                    className={`btn btn-primary btn-block btn-xl text-uppercase mt16`}
                    onClick={() => onShowLoginPopUp('signin', screens.SIGN_UP)}
                    type="button"
                    data-testid="profile.overview.sign-up-btn"
                >
                    Sign Up
                </button>
            </div>
        );
    }

    return (
        <div>
            {isLoading ? (
                <div className="pt40" data-testid="profile.overview.loading-wrapper">
                    <LoadingText text={'Loading Overview'} />
                </div>
            ) : (
                <div className={`${styles.overview}`} data-testid="profile.overview.content">
                    {showRectangleAds && (
                        <div className="d-flex jcc mt24 mb24">
                            <AdWrapper type={AdTypes.RECTANGLE_ONE} data-testid="profile.overview.ad.rectangle-1" />
                        </div>
                    )}
                    {(isCurrentUserProfile || results?.length > 0 || !auth.user) && (
                        <>
                            <h6 className={`title`} data-testid="profile.overview.recent-results-title">Recent Results</h6>
                            <div className="row">
                                {(results?.length ? results.map((result) => {
                                    const isSingles = !result?.players?.winner2 && !result?.players?.loser2;
                                    return (
                                        <div
                                            key={`score-${result.id}`}
                                            className="col-12 col-md-6"
                                        >
                                            <ScoreCard
                                                {...result}
                                                playerId={profile.playerId}
                                                showDecimalForOwn={true}
                                                gameType={isSingles ? 'singles' : 'doubles'}
                                                eligibleResults={eligibleResults}
                                                forceCardView={true}
                                                timeZoneValue={'UTC'}
                                                showVerified={false}
                                                isProProfile={isProProfile}
                                                isLeaguesMatch={result?.type === EventTypes.LEAGUES}
                                                isProfileOverviewResult={true}
                                                data-testid={`profile.overview.score-${result.id}`}
                                            />
                                        </div>
                                    );
                                }) : (
                                    <div className={`col-12`}>
                                        <p className={`text-center p48`} data-testid="profile.overview.no-results-text">No Recent Results Available</p>
                                    </div>
                                )
                                )}
                                {(isCurrentUserProfile && recentEvents?.length < 2) && (
                                    <div
                                        className={`col-12 col-md-6`}
                                    >
                                        <CtaCard
                                            data={ctaData?.POST_SCORE}
                                            onCtaClick={() => {
                                                history.push('/post-score-select');
                                            }}
                                            className="score-card"
                                            clickContext="Results tab - Post score button"
                                            clickLocation="Player Profile CTA"
                                        />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                    {isUserLoggedIn && upcomingEvents?.length > 0 && (
                        <>
                            <h6 className={`title`} data-testid="profile.overview.upcoming-events-title">Upcoming Events</h6>
                            <Events
                                auth={auth}
                                isCurrentUserProfile={isCurrentUserProfile}
                                showAll={false}
                                type={UPCOMING}
                                events={upcomingEvents}
                                force={{ cardView: true }}
                                search={search}
                                onHandleTabClick={onSeeEventsClick}
                                ctaData={ctaData}
                                isProfilePage={true}
                            />
                        </>
                    )}
                    {isUserLoggedIn && recentEvents?.length > 0 && (
                        <>
                            <h6 className={`title`} data-testid="profile.overview.recent-events-title">Recent Events</h6>
                            <Events
                                auth={auth}
                                isCurrentUserProfile={isCurrentUserProfile}
                                showAll={false}
                                type={RECENT}
                                events={recentEvents}
                                force={{ cardView: true }}
                                search={search}
                                onHandleTabClick={onSeeEventsClick}
                                ctaData={ctaData}
                            />
                        </>
                    )}
                    {showRectangleAds && (
                        <div className="d-flex jcc mt24 mb24">
                            <AdWrapper type={AdTypes.RECTANGLE_TWO} data-testid="profile.overview.ad.rectangle-2" />
                        </div>
                    )}
                    {isCurrentUserProfile && !((recentEvents?.length) || (upcomingEvents?.length > 0)) && (
                        <>
                            <h6 className={`title`} data-testid="profile.overview.upcoming-events-title">Upcoming Events</h6>
                            <div className="row">
                                <div className={`col-12 col-md-6`}>
                                    <CtaCard
                                        data={ctaData.FIND_EVENTS}
                                        onCtaClick={handleOnFindEventsClick}
                                        className="event-card"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    {(isUserLoggedIn && recentClubs !== null) && (
                        <>
                            {(isCurrentUserProfile || (recentClubs?.length > 0)) && (
                                <>
                                    <h6 className={`title`} data-testid="profile.overview.clubs-title">Clubs</h6>
                                    <div className="row">
                                        {recentClubs.map((club) => (
                                            <div
                                                className={`col-12 col-lg-6`}
                                                key={`club-${club.id}`}
                                            >
                                                <ClubCard
                                                    club={club}
                                                    force={{ cardView: true }}
                                                    data-testid={`profile.overview.club-${club.id}`}
                                                />
                                            </div>
                                        ))}
                                        {(recentClubs?.length === 0 && auth.user.isPlayer) && (
                                            <div className={`col-12 col-lg-6`}>
                                                <CtaCard
                                                    data={ctaData.CREATE_PERSONAL_CLUB}
                                                    onCtaClick={() => {
                                                        history.push('/create-players-group');
                                                    }}
                                                    className="club-card"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

OverviewTabContent.propTypes = {
    auth: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default OverviewTabContent;
